import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import useHasAccessToSummarizationAndKeywords from '../../../hooks/useHasAccessToSummarizationAndKeywords';
import { updateTranscriptionSummary } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';

const REAL_TIME_DATA_TYPE = 'call_data.transcribed_summary.openai.real_time';

const transcriptionSummarySchema = z.object({
  type: z.literal(REAL_TIME_DATA_TYPE),
  topic: z.string(),
  body: z.object({
    summary: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    generated_time: z.number(),
    is_final: z.boolean(),
  }),
});

/**
 * Custom hook that handles adding a callback to the Hermes websocket to handle
 * transcription summaries. The messages only come through when subscribed by
 * tel://phone_number.
 */
const useHandleTranscriptionSummaries = () => {
  const Hermes = window?.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const hasAccessToSummarizationAndKeywords =
    useHasAccessToSummarizationAndKeywords();

  const handleRealTimeSummarization = useCallback(
    (msg: Record<string, unknown>) => {
      const parsed = transcriptionSummarySchema.safeParse(msg);
      if (!parsed.success) return;

      const { topic, body } = parsed.data;
      const callerId = topic.split('tel://')[1] ?? '';

      // Add default values to summaries
      const normalizedSummary = {
        summary: body.summary ?? '',
        keywords: body.keywords ?? [],
        generated_time: body.generated_time,
        is_final: body.is_final,
      };

      dispatch(
        updateTranscriptionSummary({
          callerId,
          summary: normalizedSummary,
          keywords: normalizedSummary.keywords,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({
        ...(hasAccessToSummarizationAndKeywords
          ? {
              handleRealTimeSummarization: handleRealTimeSummarization,
            }
          : {}),
      });
    }
  }, [
    Hermes,
    handleRealTimeSummarization,
    hasAccessToSummarizationAndKeywords,
  ]);

  return null;
};

export default useHandleTranscriptionSummaries;
